/**
 * The toolbar above a list, as finixt-ui's TableToolbar draws it: search on
 * the left, the chip row beside it, a mono count and the actions on the
 * right, a hairline below.
 *
 * The count is the point. Every list here filters client-side over a fully
 * fetched collection, so "12 of 340" is the only thing that tells an operator
 * whether an empty table means nothing matches or nothing exists. The chips
 * are a single choice, which finixt-ui's toolbar does not model, so the row
 * is composed here from its FilterChip.
 */
import type { ReactNode } from 'react';

import { FilterChip, SearchField } from './finixt.js';

export interface ListToolbarProps {
  search?: string | undefined;
  onSearch?: ((value: string) => void) | undefined;
  placeholder?: string | undefined;
  filters?: ReactNode;
  count?: ReactNode;
  actions?: ReactNode;
  /** No hairline and no side padding: a toolbar standing alone above a table, not inside a card. */
  bare?: boolean | undefined;
}

export function ListToolbar({
  search,
  onSearch,
  placeholder = 'Search…',
  filters,
  count,
  actions,
  bare = false,
}: ListToolbarProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 11,
        padding: bare ? '11px 0' : '11px 14px',
        borderBottom: bare ? undefined : '1px solid var(--border-hairline)',
      }}
    >
      {onSearch ? (
        <div style={{ width: 256, maxWidth: '100%' }}>
          <SearchField
            value={search ?? ''}
            onChange={onSearch}
            placeholder={placeholder}
            size="sm"
          />
        </div>
      ) : null}
      {filters}
      {count || actions ? (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 11 }}>
          {count ? (
            <span
              className="mono"
              style={{ fontSize: 'var(--text-label-lg)', color: 'var(--text-faint)' }}
            >
              {count}
            </span>
          ) : null}
          {actions}
        </div>
      ) : null}
    </div>
  );
}

export interface ChipFilterOption<T extends string> {
  key: T;
  label: string;
  count?: number | undefined;
}

export interface ChipFilterProps<T extends string> {
  options: ReadonlyArray<ChipFilterOption<T>>;
  value: T;
  onChange: (next: T) => void;
}

/** Single-choice chip row: the closed-set filter every list page carries. */
export function ChipFilter<T extends string>({ options, value, onChange }: ChipFilterProps<T>) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 7 }}>
      {options.map((option) => (
        <FilterChip
          key={option.key}
          label={option.label}
          active={value === option.key}
          onClick={() => onChange(option.key)}
          {...(option.count !== undefined ? { count: option.count } : {})}
        />
      ))}
    </div>
  );
}
